import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Sun, Moon, Leaf, Check } from 'lucide-react';
import { cn } from '../../lib/utils';

type ThemeOption = 'light' | 'dark' | 'moss';

interface NotificationSettings {
  enabled: boolean;
  soundEnabled: boolean;
}

interface UserSettingsPageProps {
  hasOrcamentistaRole: boolean;
  notificationSettings: NotificationSettings;
  updateNotificationSettings: (updates: Partial<NotificationSettings>) => void;
  theme: ThemeOption;
  setTheme: (theme: ThemeOption) => void;
}

const THEME_OPTIONS: { value: ThemeOption; label: string; description: string; icon: typeof Sun; preview: { bg: string; card: string; accent: string } }[] = [
  {
    value: 'light',
    label: 'Claro',
    description: 'Fundo branco com texto escuro',
    icon: Sun,
    preview: { bg: 'bg-gray-100', card: 'bg-white', accent: 'bg-blue-500' },
  },
  {
    value: 'dark',
    label: 'Escuro',
    description: 'Fundo grafite com texto claro',
    icon: Moon,
    preview: { bg: 'bg-zinc-900', card: 'bg-zinc-800', accent: 'bg-blue-500' },
  },
  {
    value: 'moss',
    label: 'Moss',
    description: 'Tons de verde musgo, cantos arredondados',
    icon: Leaf,
    preview: { bg: 'bg-[hsl(140,8%,12%)]', card: 'bg-[hsl(140,10%,15%)]', accent: 'bg-[hsl(140,25%,55%)]' },
  },
];

export function UserSettingsPage({
  hasOrcamentistaRole,
  notificationSettings,
  updateNotificationSettings,
  theme,
  setTheme,
}: UserSettingsPageProps) {
  return (
    <>
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <span>Minha Conta</span>
        <span>/</span>
        <span className="font-medium text-foreground">Configurações</span>
      </div>

      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle>Minhas Configurações</CardTitle>
          <CardDescription>
            Configure suas preferências pessoais.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-8">
          {/* Theme Picker */}
          <div>
            <h3 className="text-lg font-semibold mb-2">Tema</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Escolha o tema padrão da interface.
            </p>
            <div className="grid gap-3 sm:grid-cols-3">
              {THEME_OPTIONS.map((opt) => {
                const Icon = opt.icon;
                const isSelected = theme === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setTheme(opt.value)}
                    className={cn(
                      "relative flex flex-col items-start gap-3 rounded-lg border-2 p-4 text-left transition-all",
                      "hover:border-primary/50",
                      isSelected
                        ? "border-primary bg-primary/5"
                        : "border-border"
                    )}
                  >
                    {isSelected && (
                      <div className="absolute top-2 right-2 flex h-5 w-5 items-center justify-center rounded-full bg-primary">
                        <Check className="h-3 w-3 text-primary-foreground" />
                      </div>
                    )}
                    {/* Mini preview */}
                    <div className={cn("w-full h-12 rounded-md flex items-end gap-1.5 p-2", opt.preview.bg)}>
                      <div className={cn("flex-1 h-5 rounded-sm", opt.preview.card)} />
                      <div className={cn("w-8 h-5 rounded-sm", opt.preview.accent)} />
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium text-sm">{opt.label}</span>
                    </div>
                    <span className="text-xs text-muted-foreground leading-relaxed">
                      {opt.description}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {hasOrcamentistaRole && (
            <div>
              <h3 className="text-lg font-semibold mb-4">Notificações de Novos Orçamentos</h3>
              <p className="text-sm text-muted-foreground mb-6">
                Configure como você deseja ser notificado quando um novo orçamento chega.
              </p>

              <div className="space-y-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="space-y-1">
                    <Label>Notificações do Navegador</Label>
                    <p className="text-xs text-muted-foreground">
                      Receba notificações no navegador mesmo quando a aba não estiver ativa
                    </p>
                  </div>
                  <Switch
                    checked={notificationSettings.enabled}
                    onCheckedChange={(checked) => {
                      updateNotificationSettings({ enabled: checked });
                      if (checked && 'Notification' in window && Notification.permission === 'default') {
                        Notification.requestPermission();
                      }
                    }}
                  />
                </div>

                <div className="flex items-center justify-between gap-4">
                  <div className="space-y-1">
                    <Label>Alertas Sonoros</Label>
                    <p className="text-xs text-muted-foreground">
                      Reproduzir um som discreto quando um novo orçamento chegar
                    </p>
                  </div>
                  <Switch
                    checked={notificationSettings.soundEnabled}
                    onCheckedChange={(checked) => {
                      updateNotificationSettings({ soundEnabled: checked });
                    }}
                  />
                </div>

                {notificationSettings.enabled && 'Notification' in window && Notification.permission !== 'granted' && (
                  <div className="rounded-lg border border-orange-200 bg-orange-50 p-4 dark:border-orange-900 dark:bg-orange-950">
                    <p className="text-sm text-orange-700 dark:text-orange-300">
                      As notificações do navegador estão ativadas, mas você ainda não concedeu permissão.
                      Por favor, clique em "Permitir" quando o navegador solicitar.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}
